import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';
import RowsPerPageSelect from './RowsPerPageSelect';

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  currentPage: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  emptyMessage?: string;
}

export function DataTable<T extends { _id: string }>({
  data,
  columns,
  currentPage,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
  emptyMessage = 'No data found',
}: DataTableProps<T>) {
  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <div className="space-y-4 min-w-0">
      {/* Table */}
      <div className="w-full overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="group">
              {columns.map((col, colIndex) => (
                <TableHead
                  key={col.key}
                  className={cn(
                    'transition-colors',
                    col.className,
                    col.className?.includes('sticky') && '!bg-background group-hover:!bg-muted',
                    // Add top-right radius to last header cell if it's sticky
                    colIndex === columns.length - 1 &&
                      col.className?.includes('sticky') &&
                      'rounded-tr-md'
                  )}
                >
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center py-8">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, rowIndex) => (
                <TableRow key={row._id} className="group">
                  {columns.map((col, colIndex) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        'transition-colors',
                        col.className,
                        col.className?.includes('sticky') && '!bg-background group-hover:!bg-muted',
                        // Add bottom-right radius to last cell of last row if it's sticky
                        rowIndex === data.length - 1 &&
                          colIndex === columns.length - 1 &&
                          col.className?.includes('sticky') &&
                          'rounded-br-md'
                      )}
                    >
                      {col.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {(rowsPerPage < total || totalPages > 1) && (
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
          {/* Rows per page */}
          <RowsPerPageSelect
            rowsPerPage={rowsPerPage}
            onChange={(rows) => {
              onRowsPerPageChange(rows);
              onPageChange(1);
            }}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between sm:justify-start items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-9 w-9 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="h-9 w-9 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
